import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { ArticleCard, TranslationDto } from '@b4b/shared';
import { useTranslateArticle, type TranslateResult } from '../api/queries.js';

/** How many "queued" (202) answers we re-poll before giving up on the body. */
export const MAX_POLL_ATTEMPTS = 6;
/** Delay between two polls, in milliseconds. */
export const POLL_DELAY_MS = 5_000;

/**
 * Pure: does this article still need its body translated?
 * The caller folds premium/showOriginal (and "there is a body at all") into `enabled`.
 *
 * Reads the card translation the list attached, never a body the POST returned — a
 * returned body always settles the article (see `translationReducer`). `contentStatus`
 * is 'none' on every fresh card row, so it cannot be terminal here.
 */
export function shouldRequestFullText(article: ArticleCard | undefined, enabled: boolean): boolean {
  if (!enabled || !article) return false;
  const translation = article.translation;
  if (!translation) return false;
  return (
    translation.contentHtml === null &&
    translation.contentStatus !== 'failed' &&
    article.contentStatus === 'ok'
  );
}

/** Pure: delay before the next poll after `attempts` queued answers, null once we give up. */
export function nextPollDelay(attempts: number): number | null {
  return attempts < MAX_POLL_ATTEMPTS ? POLL_DELAY_MS : null;
}

/** Per-article state of the on-demand body translation. */
export interface TranslationEntry {
  /** queued (202) answers seen so far */
  attempts: number;
  /** gave up: too many polls, a transport error, or the server answered 'failed' */
  failed: boolean;
  /** the body the server returned, null until a 200 arrives */
  body: TranslationDto | null;
  /** the server answered for good (ok, failed or 'none') — never ask again */
  settled: boolean;
}

export interface TranslationState {
  entries: Record<string, TranslationEntry>;
  /** article id whose POST is running, null when nothing is in flight */
  inFlight: string | null;
  /** article id waiting for the poll timer, null when no poll is scheduled */
  pendingPollFor: string | null;
}

export type TranslationEvent =
  | { type: 'start'; articleId: string }
  | { type: 'response'; articleId: string; result: TranslateResult }
  | { type: 'error'; articleId: string }
  | { type: 'poll-due'; articleId: string }
  | { type: 'retry'; articleId: string }
  | { type: 'article-changed'; articleId: string };

export const initialTranslationState: TranslationState = {
  entries: {},
  inFlight: null,
  pendingPollFor: null,
};

const EMPTY_ENTRY: TranslationEntry = { attempts: 0, failed: false, body: null, settled: false };

function entryOf(state: TranslationState, id: string): TranslationEntry {
  return state.entries[id] ?? EMPTY_ENTRY;
}

function withEntry(state: TranslationState, id: string, entry: TranslationEntry): TranslationState['entries'] {
  return { ...state.entries, [id]: entry };
}

/**
 * Pure state machine of the on-demand translation, keyed by article id.
 *
 * Everything the hook decides lives here: how many queued answers we tolerate, when a
 * poll is due, and when an article is settled. The hook around it only performs the
 * request and owns the timer.
 */
export function translationReducer(state: TranslationState, event: TranslationEvent): TranslationState {
  const id = event.articleId;
  switch (event.type) {
    case 'start':
      return {
        entries: withEntry(state, id, entryOf(state, id)),
        inFlight: id,
        pendingPollFor: state.pendingPollFor === id ? null : state.pendingPollFor,
      };

    case 'response': {
      const entry = entryOf(state, id);
      const inFlight = state.inFlight === id ? null : state.inFlight;
      if ('status' in event.result) {
        // 202: the server is still working on it — poll again until we run out of attempts
        const giveUp = nextPollDelay(entry.attempts) === null;
        return {
          entries: withEntry(state, id, { ...entry, attempts: entry.attempts + 1, failed: giveUp }),
          inFlight,
          pendingPollFor: giveUp ? (state.pendingPollFor === id ? null : state.pendingPollFor) : id,
        };
      }
      // 200: 'ok' is the body, 'failed' says the server gave up, 'none' says the article
      // lost its extracted text — all three are final, so the banner stops either way
      return {
        entries: withEntry(state, id, {
          ...entry,
          body: event.result,
          failed: event.result.contentStatus === 'failed',
          settled: true,
        }),
        inFlight,
        pendingPollFor: state.pendingPollFor === id ? null : state.pendingPollFor,
      };
    }

    case 'error':
      return {
        entries: withEntry(state, id, { ...entryOf(state, id), failed: true }),
        inFlight: state.inFlight === id ? null : state.inFlight,
        pendingPollFor: state.pendingPollFor === id ? null : state.pendingPollFor,
      };

    case 'poll-due':
      if (state.pendingPollFor !== id) return state;
      return { ...state, pendingPollFor: null };

    case 'retry':
      return {
        entries: withEntry(state, id, { ...EMPTY_ENTRY }),
        inFlight: state.inFlight,
        pendingPollFor: state.pendingPollFor === id ? null : state.pendingPollFor,
      };

    case 'article-changed': {
      const entry = state.entries[id];
      // a body we already hold stays (it is final); everything else starts from scratch,
      // so switching sources never inherits the previous attempt count or failure
      const next: TranslationEntry = entry?.body
        ? entry
        : { ...EMPTY_ENTRY };
      return { entries: withEntry(state, id, next), inFlight: state.inFlight, pendingPollFor: null };
    }

    default:
      return state;
  }
}

export interface ArticleTranslation {
  /** Body translation once fetched, otherwise the card translation the server attached. */
  translation: TranslationDto | null;
  /** A body translation is being fetched or polled right now. */
  translating: boolean;
  /** This article's body translation gave up; the original is shown instead. */
  failed: boolean;
  retry: () => void;
}

/**
 * On-demand full-text translation for the article currently on screen.
 *
 * A thin shell over `translationReducer`: it performs the POST, owns the poll timer and
 * keeps an in-flight ref so StrictMode's double mount cannot fire a second request. All
 * state is keyed by article id, so switching sources inside a cluster never inherits the
 * previous article's failure or attempt count.
 */
export function useArticleTranslation(
  article: ArticleCard | undefined,
  enabled: boolean,
): ArticleTranslation {
  const translate = useTranslateArticle();
  const [state, dispatch] = useReducer(translationReducer, initialTranslationState);

  const inFlightRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const mutateRef = useRef(translate.mutate);
  mutateRef.current = translate.mutate;

  const articleId = article?.id;
  const entry = articleId !== undefined ? state.entries[articleId] : undefined;
  const translation: TranslationDto | null = entry?.body ?? article?.translation ?? null;
  const failed = entry?.failed === true;
  const wants = shouldRequestFullText(article, enabled) && !failed && entry?.settled !== true;

  // readable from the retry callback without a stale closure
  const latestIdRef = useRef<string | null>(articleId ?? null);
  latestIdRef.current = articleId ?? null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (articleId === undefined) return;
    dispatch({ type: 'article-changed', articleId });
  }, [articleId]);

  // request: one POST per article at a time; a finished request re-runs this effect
  // (state.inFlight changes), which is what starts the article the reader switched to
  useEffect(() => {
    if (articleId === undefined || !wants) return;
    if (state.pendingPollFor !== null) return;
    if (inFlightRef.current !== null) return;
    inFlightRef.current = articleId;
    dispatch({ type: 'start', articleId });
    mutateRef.current(articleId, {
      onSuccess: (result) => {
        inFlightRef.current = null;
        if (!mountedRef.current) return;
        dispatch({ type: 'response', articleId, result });
      },
      onError: () => {
        inFlightRef.current = null;
        if (!mountedRef.current) return;
        dispatch({ type: 'error', articleId });
      },
    });
  }, [articleId, wants, state.inFlight, state.pendingPollFor]);

  // poll timer: cleared on unmount, on article switch and when the original is shown
  useEffect(() => {
    const pollFor = state.pendingPollFor;
    if (pollFor === null || !wants) return;
    const timer = setTimeout(() => dispatch({ type: 'poll-due', articleId: pollFor }), POLL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [state.pendingPollFor, wants]);

  const retry = useCallback(() => {
    const id = latestIdRef.current;
    if (id === null) return;
    dispatch({ type: 'retry', articleId: id });
  }, []);

  return { translation, translating: wants, failed, retry };
}
